import { useMemo } from 'react'

import { matchFilterArrayIncludes, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import type { UseLoadMorePayload } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { PortalModel } from '@models/types'

import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['portals']

type PortalsFilters = {
  exclude_ids?: number[],
  export_enabled?: 'true' | 'false',
  string?: string,
}

interface UsePortalsOptions {
  entityKey?: string,
  filters?: PortalsFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

export type UsePortalsPayload = {
  callbacks: {
    loadMore: Function,
  },
  canLoadMore: boolean,
  filteredPortals: PortalModel[],
  filteredPortalsCount: number,
  hasFilteredPortals: boolean,
  loading: boolean,
}

function usePortals(options: UsePortalsOptions = {}): UsePortalsPayload {
  const { filters, requestOptions } = options
  const {
    exclude_ids: filterExcludeIds,
    export_enabled: filterExportEnabled,
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: { portals: portalsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const portals = useSelector(reduxState => reduxState.entities.portals)

  const filteredPortals = useMemo((): PortalModel[] => {
    const filtered = Object.values(portals).filter((portal) => {
      const { export_enabled, id, title } = portal

      const matchExcludedIds = filterExcludeIds
        ? !matchFilterArrayIncludes(filterExcludeIds, id)
        : true

      const matchExportEnabled = filterExportEnabled ? export_enabled?.toString() === filterExportEnabled : true

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchExcludedIds && matchExportEnabled && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [portalsUpdatedAt, JSON.stringify(filters)])

  const filteredPortalsCount = filteredPortals.length
  const hasFilteredPortals = !!filteredPortalsCount

  const loadMorePayload: UseLoadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredPortalsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingPortals } = useReduxAction(
    'portals',
    'loadPortals',
    {
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredPortals,
    filteredPortalsCount,
    hasFilteredPortals,
    loading: loadingPortals,
  }
}

export default usePortals
